import changeCase from 'change-case'
import moment from 'moment'
import { allIconNames } from '@opallabs/constellation'

export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ'

// If the channel system name does not match the channel icon name, they
// must be mapped here in order for channel icons to appear correctly
// in the Personal Panel. Otherwise, the default star icon will be used.
export const channelNameMaps = {
  att_web: 'blog',
  digital_advertising: 'display-ad',
  email_preview: 'email',
  google_plus: 'googleplus',
  linked_in: 'linkedin',
  longform: 'all',
  modular_email: 'email',
  radio: 'audio',
  sharepoint: 'website',
  television: 'video',
  web: 'website',
  web_preview: 'web-modules',
  web_modules: 'web-modules',
  you_tube: 'youtube',
  sms_message: 'sms',
}

export const gemChannelNameMaps = {
  ...channelNameMaps,
  digital_advertising: 'digital-ad',
}

// Takes a string and checks it against a whitelist
// of service names to return a Universe-friendly
// service name.
// If no match is found, a default service name is
// returned. This is needed because the way that
// we store service names varies quite a bit. This
// normalizes them to adhere to Universe's conventions.
export const decorateServiceName = (
  name,
  defaultName = 'star',
  format = false,
  sanitizeName = false
) => {
  if (!name) return defaultName

  const sanitizedName = sanitizeName ? name.replace(/^\w+\s/, '') : name
  let channelName

  if (allIconNames[`channel-${sanitizedName}-14`]) {
    channelName = sanitizedName
  }

  if (channelNameMaps[sanitizedName]) {
    channelName = channelNameMaps[sanitizedName]
  }

  if (sanitizedName.includes('web_modules')) {
    channelName = 'web-modules'
  }

  if (!channelName) return `channel-${defaultName}-14`

  return format ? `channel-${channelName}-14` : channelName
}

export const avatarsDecorator = (avatar) => ({
  ...avatar,
  full_url: avatar.url,
})

export const userDecorator = (user) => ({
  ...user,
  firstName: user.first_name,
  fullName: user.full_name || `${user.first_name} ${user.last_name}`,
  lastName: user.last_name,
  // title is used for people pickers
  title: `${user.first_name} ${user.last_name}`,
})

const itemDecorator = (item) => ({
  created_at: moment(item.created_at, DEFAULT_DATE_FORMAT),
  updated_at: moment(item.updated_at, DEFAULT_DATE_FORMAT),
})

export const taskDecorator = (task) => ({
  ...task,
  timestamp: moment(task.created_at, DEFAULT_DATE_FORMAT),
  status: task.is_approved ? 'complete' : 'pending',
  ...itemDecorator(task),
})

const approvalsDecorator = (approval) => {
  const isApproved = approval.is_approved === true && approval.is_new === false
  const isDeclined = approval.is_approved === false && approval.is_new === false

  return {
    ...approval,
    completedAt: !approval.is_new ? approval.updated_at : '',
    isApproved,
    isDeclined,
    ...itemDecorator(approval),
  }
}

const responseDecorator = (response) => ({
  ...response,
  completedAt: response.completed_at,
  createdBy: response.created_by,
  updatedAt: response.updated_at,
})

const camelDecorator = (obj) => {
  const transformed = {}
  const keys = Object.keys(obj)

  keys.forEach((key) => {
    const camelKey = changeCase.camelCase(key)
    transformed[camelKey] = obj[key]
  })

  return transformed
}

const momentDecorator = (theMoment) => ({
  ...theMoment,
  name: theMoment.title,
  postCount:
    theMoment.relationships.posts && theMoment.relationships.posts.length,
})

const decorators = {
  approvals: approvalsDecorator,
  avatars: avatarsDecorator,
  channels: camelDecorator,
  contents: camelDecorator,
  'content-types': camelDecorator,
  decorateServiceName,
  displays: camelDecorator,
  moment: momentDecorator,
  'option-data': camelDecorator,
  response: responseDecorator,
  'section-data': camelDecorator,
  sections: camelDecorator,
  slide: camelDecorator,
  tasks: taskDecorator,
  users: userDecorator,
  user: userDecorator,
}

export default decorators
